import {
  canCreateDocuments,
  canEditAllDocuments,
  canModifyDocument,
  hasAnyPermission,
  hasPermission,
  type Permission,
  type Section,
  canSeeSection,
} from "@xop/dashboard-shared";

import { useAuth } from "./AuthProvider.js";

/** React-side permission helpers. Thin wrappers over the shared matrix
 * so the same rules run on server + client without drift. */

export function useCan(perm: Permission): boolean {
  const { user } = useAuth();
  return hasPermission(user?.permissions, perm);
}

export function useCanAny(perms: readonly Permission[]): boolean {
  const { user } = useAuth();
  return hasAnyPermission(user?.permissions, perms);
}

export function useCanSeeSection(section: Section): boolean {
  const { user } = useAuth();
  return canSeeSection(user?.permissions, section);
}

/** A document carrying enough to decide ownership (its author id). */
type OwnableDoc = { author?: { id: number } | null };

export interface DocumentCapabilities {
  /** May create NEW documents (create or edit tier). Gates the "+ Add" /
   *  "Create the first one" affordances. */
  canCreate: boolean;
  /** Full editor — may edit/delete/publish ANY doc + manage categories. */
  canEditAll: boolean;
  /** May this specific doc be edited/deleted/published by the current user?
   *  True for full editors on any doc, or for create-tier authors on docs
   *  they wrote. A pure closure (NOT a hook) so it's safe to call per row. */
  canModify: (doc: OwnableDoc) => boolean;
}

/**
 * Resolve the current user's documentation authoring capabilities once. The
 * three-tier model (view < create < edit) lives in `@xop/dashboard-shared`;
 * this binds it to the signed-in user (permissions + id for ownership). Call
 * it ONCE per component and apply `canModify` per row — it's a plain function,
 * not a hook, so it's loop-safe.
 */
export function useDocumentCapabilities(): DocumentCapabilities {
  const { user } = useAuth();
  const perms = user?.permissions;
  const userId = user?.id ?? null;
  return {
    canCreate: canCreateDocuments(perms),
    canEditAll: canEditAllDocuments(perms),
    canModify: (doc: OwnableDoc) =>
      canModifyDocument(perms, { authorId: doc.author?.id ?? null, userId }),
  };
}
